// Compresión client-side de imágenes antes de subirlas.
//
// Por qué existe: el admin enviaba las fotos en resolución original como
// base64 dentro del JSON (una foto de teléfono de 4MB -> ~5.3MB de body,
// con bodyLimit de 10MB en la API). Un PUT con 2-3 fotos tardaba 6+ segundos.
// Redimensionando a 1600px (suficiente para web) el body cae ~90% y el
// contrato con la API no cambia: sigue siendo un data URL (data:image/...).
//
// Reglas:
// - Solo archivos de imagen (no GIF: redimensionar mataría la animación).
// - Si la imagen ya entra en maxDimension, se devuelve null para usar el
//   original tal cual (cero recompresión, cero pérdida).
// - Cualquier error -> null (el llamador usa el original como fallback).

const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.82;

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });

const blobToDataURL = (blob) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });

export const compressImageFile = async (
  file,
  { maxDimension = MAX_DIMENSION, quality = JPEG_QUALITY } = {}
) => {
  if (
    !file ||
    typeof File === "undefined" ||
    !(file instanceof File) ||
    typeof file.type !== "string" ||
    !file.type.startsWith("image/") ||
    file.type === "image/gif"
  ) {
    return null;
  }

  let img;
  try {
    img = await loadImage(file);
  } catch (e) {
    return null;
  }

  const { naturalWidth: w, naturalHeight: h } = img;
  if (!w || !h || Math.max(w, h) <= maxDimension) {
    return null;
  }

  const scale = maxDimension / Math.max(w, h);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return null;
  }
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  const mime = file.type === "image/png" ? "image/png" : "image/jpeg";
  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, mime, quality)
  );
  if (!blob) {
    return null;
  }
  const dataURL = await blobToDataURL(blob);
  return typeof dataURL === "string" && dataURL.startsWith("data:image")
    ? dataURL
    : null;
};
